import React, { useEffect, useRef, useState } from 'react';
import { Dropdown, Input } from 'semantic-ui-react';
import { DocumentFilterOption, DocumentListFilters } from '../../types/DocumentFilters';
import { hasActiveDropdownFilters } from '../../utils/document';
import './style.less';

const SEARCH_DEBOUNCE_MS = 700;

interface Props {
    filters: DocumentListFilters;
    serviceOptions: DocumentFilterOption[];
    assetOptions: DocumentFilterOption[];
    groupOptions: DocumentFilterOption[];
    loadingOptions?: boolean;
    hideGroupFilter?: boolean;
    onFiltersChange: (filters: DocumentListFilters) => void;
}

const DocumentFilters = ({
    filters,
    serviceOptions,
    assetOptions,
    groupOptions,
    loadingOptions = false,
    hideGroupFilter = false,
    onFiltersChange,
}: Props) => {
    const [searchDraft, setSearchDraft] = useState(filters.searchText);
    const [dropdownResetKey, setDropdownResetKey] = useState(0);
    const searchTimeoutRef = useRef<number>();
    // Tracks the latest `filters` so the debounced commit below can merge into whatever
    // is current when it actually fires, rather than the (possibly stale) `filters` it
    // closed over when the timer was scheduled — otherwise a dropdown change made while
    // a search debounce is still pending gets silently reverted when the timer fires.
    const latestFiltersRef = useRef(filters);
    latestFiltersRef.current = filters;

    useEffect(() => {
        setSearchDraft(filters.searchText);
    }, [filters.searchText]);

    // When the group filter is hidden (e.g. the single-group tab, where the sidebar
    // already fixes the group), its groupId is a fixed context value, not a user-chosen
    // filter — so it shouldn't count toward "is a filter active" checks below.
    const activeFilterCheck = hideGroupFilter ? { ...filters, groupId: '' } : filters;

    useEffect(() => {
        if (!hasActiveDropdownFilters(activeFilterCheck)) {
            setDropdownResetKey((key) => key + 1);
        }
    }, [filters.serviceId, filters.assetId, hideGroupFilter ? '' : filters.groupId]);

    // Auto-search after the resident pauses typing, like a normal search box —
    // Enter still commits immediately for anyone who prefers that.
    useEffect(() => {
        if (searchDraft === filters.searchText) {
            return undefined;
        }
        searchTimeoutRef.current = window.setTimeout(() => {
            onFiltersChange({ ...latestFiltersRef.current, searchText: searchDraft });
        }, SEARCH_DEBOUNCE_MS);
        return () => window.clearTimeout(searchTimeoutRef.current);
    }, [searchDraft]);

    const applySearch = () => {
        window.clearTimeout(searchTimeoutRef.current);
        onFiltersChange({
            ...filters,
            searchText: searchDraft,
        });
    };

    const updateDropdownFilter = (field: 'serviceId' | 'assetId' | 'groupId', value: string) => {
        onFiltersChange({
            ...filters,
            [field]: value,
        });
    };

    return (
        <div className="documentsFiltersBar">
            <div className="documentsSearchRow">
                <Input
                    className="documentsSearchInput"
                    icon="search"
                    iconPosition="left"
                    placeholder="Search documents…"
                    value={searchDraft}
                    onChange={(event) => setSearchDraft(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            applySearch();
                        }
                    }}
                />
            </div>
            <div className="documentsFilterDropdowns">
                <Dropdown
                    key={`service-${dropdownResetKey}-${filters.serviceId || 'none'}`}
                    placeholder="Filter by service"
                    selection
                    clearable
                    search
                    selectOnBlur={false}
                    selectOnNavigation={false}
                    loading={loadingOptions}
                    options={serviceOptions}
                    value={filters.serviceId || undefined}
                    onChange={(_event, { value }) => updateDropdownFilter('serviceId', String(value || ''))}
                />
                <Dropdown
                    key={`asset-${dropdownResetKey}-${filters.assetId || 'none'}`}
                    placeholder="Filter by asset"
                    selection
                    clearable
                    search
                    selectOnBlur={false}
                    selectOnNavigation={false}
                    loading={loadingOptions}
                    options={assetOptions}
                    value={filters.assetId || undefined}
                    onChange={(_event, { value }) => updateDropdownFilter('assetId', String(value || ''))}
                />
                {!hideGroupFilter && (
                    <Dropdown
                        key={`group-${dropdownResetKey}-${filters.groupId || 'none'}`}
                        placeholder="Filter by group"
                        selection
                        clearable
                        search
                        selectOnBlur={false}
                        selectOnNavigation={false}
                        loading={loadingOptions}
                        options={groupOptions}
                        value={filters.groupId || undefined}
                        onChange={(_event, { value }) => updateDropdownFilter('groupId', String(value || ''))}
                    />
                )}
            </div>
        </div>
    );
};

export default DocumentFilters;
